import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  OutlinedInput,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';

import './GeneralTable.css';
import axiosInstance from '../../initializers/axios';
import { ApiConstants } from '../../constants/ApiConstants';
import IPagination from '../../interfaces/IPagination';
import IGridItems from '../../interfaces/IGridItems';
import { useTranslation } from 'react-i18next';
import { isValid } from 'date-fns';
import useGetUser from '../../hooks/useGetUser';
import { formatDateByTimeZone, getCompanyTimeZone } from '../../utils/timezone';
import { useAppTheme } from '../../theme/AppThemeContext';

interface ExtraTableAction {
  key: string;
  title: string;
  icon: React.ReactNode;
  onClick: (item: any) => void;
  show?: (item: any) => boolean;
  className?: string;
}

interface Props {
  data: any[];
  title?: string | null;
  hasRp?: boolean;
  children?: React.ReactNode;
  columns: IGridItems[];
  gridEntity: number;
  pagination: IPagination;
  selectedItem: (val: any) => void;
  onClickConfirm?: () => void;
  onClickCancel?: () => void;
  onEdit?: (item: any) => void;
  onDelete?: (item: any) => void;
  canEdit?: (item: any) => boolean;
  canDelete?: (item: any) => boolean;
  setComplete?: (item: any) => void;
  isCompleteShown?: boolean;
  extraActions?: ExtraTableAction[];
  getRowClassName?: (item: any) => string;
  renderCell?: (row: any, column: IGridItems) => React.ReactNode;
  mobileCardColumns?: string[];
  selectedItemPagination: (val: any) => void;
  onFilterClose?: (gridItems: IGridItems[]) => void;
}

type CustomizeTab = 'columns' | 'filters';

const MAX_ACTIVE_FILTERS = 3;

const GeneralTable: FC<Props> = ({
  data,
  title,
  hasRp,
  children,
  columns,
  gridEntity,
  selectedItem,
  pagination,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  setComplete,
  isCompleteShown,
  extraActions = [],
  getRowClassName,
  renderCell,
  mobileCardColumns,
  selectedItemPagination,
  onFilterClose,
}) => {
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [activeFilterEntities, setActiveFilterEntities] = useState<string[]>([]);
  const [pg, setPg] = useState<IPagination>(pagination);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeTab, setCustomizeTab] = useState<CustomizeTab>('columns');
  const [showMobileQuickFilters, setShowMobileQuickFilters] = useState(false);
  const quickFilterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const user = useGetUser();
  const companyTimeZone = getCompanyTimeZone(user);

  const { i18n } = useTranslation();
  const { theme } = useAppTheme();

  const isMobileOrTablet = useMediaQuery('(max-width:1024px)');
  const MOBILE_TRUNCATE = 10;

  const isIsoDateLike = (s: string) => /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(s);
  const isSyntheticColumn = (entity: string) => entity.startsWith('__');

  const tryFormatDate = (val: unknown): string | null => {
    if (val instanceof Date && isValid(val)) {
      return formatDateByTimeZone(val, companyTimeZone);
    }

    if (typeof val === 'string' && isIsoDateLike(val)) {
      const formatted = formatDateByTimeZone(val, companyTimeZone);
      if (formatted) {
        return formatted;
      }
    }

    return null;
  };

  const toDisplayString = (val: any): string => {
    if (val === null || val === undefined) return '';

    const asDate = tryFormatDate(val);
    if (asDate) return asDate;

    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);

    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  };

  const truncateIfMobile = (text: string, shouldTruncate = true) => {
    if (!isMobileOrTablet) return text;
    if (!shouldTruncate) return text;
    if (text.length <= MOBILE_TRUNCATE) return text;
    return text.slice(0, MOBILE_TRUNCATE) + '...';
  };

  const renderValue = (row: any, entity: string, shouldTruncate = true) => {
    const v = getValueByCaseInsensitiveKey(row, entity);
    const formattedDate = tryFormatDate(v);
    const text = formattedDate ?? toDisplayString(v);
    return truncateIfMobile(text, shouldTruncate && !formattedDate);
  };

  const renderCellValue = (row: any, column: IGridItems, shouldTruncate = true) =>
    renderCell?.(row, column) ?? renderValue(row, column.entity, shouldTruncate);
  const getMobileCardFieldClassName = (row: any, column: IGridItems) => {
    const rawValue = getValueByCaseInsensitiveKey(row, column.entity);
    const valueText = toDisplayString(rawValue);
    const labelText = String(i18n.t(`entity.${column.label}`) ?? column.label ?? '');
    const entity = column.entity.toLowerCase();
    const shouldSpanFull =
      valueText.length > 24 ||
      labelText.length > 18 ||
      entity.includes('description') ||
      entity.includes('comment') ||
      entity.includes('address') ||
      entity.includes('note') ||
      entity.includes('subject') ||
      entity.includes('fiscal') ||
      entity.includes('ebills');

    return `gtCardRow ${shouldSpanFull ? 'gtCardRow--wide' : ''}`.trim();
  };

  useEffect(() => {
    setPg(pagination);
  }, [pagination]);

  useEffect(() => {
    setHiddenColumns(columns.filter((c) => !c.isShown).map((c) => c.entity));

    const nextFilters = columns.reduce<Record<string, string>>((acc, c) => {
      acc[c.entity] = c.filter?.value ?? '';
      return acc;
    }, {});
    setColumnFilters(nextFilters);

    const nextActiveFilters = columns
      .filter((c) => c.filter !== null && c.filter !== undefined)
      .map((c) => c.entity)
      .slice(0, MAX_ACTIVE_FILTERS);

    setActiveFilterEntities(nextActiveFilters);
  }, [columns]);

  useEffect(() => {
    return () => {
      if (quickFilterDebounceRef.current) {
        clearTimeout(quickFilterDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobileOrTablet || activeFilterEntities.length === 0) {
      setShowMobileQuickFilters(false);
    }
  }, [isMobileOrTablet, activeFilterEntities]);

  const visibleColumns = useMemo(() => columns.filter((c) => !hiddenColumns.includes(c.entity)), [columns, hiddenColumns]);
  const mobileCardVisibleColumns = useMemo(() => {
    if (!mobileCardColumns?.length) {
      return visibleColumns;
    }

    const normalize = (value: string) =>
      String(value ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    const wantedOrder = mobileCardColumns.map(normalize);
    const wanted = new Set(wantedOrder);

    return visibleColumns
      .filter((column) => wanted.has(normalize(column.entity)) || wanted.has(normalize(column.label)))
      .sort((a, b) => {
        const aIndex = wantedOrder.findIndex((key) => key === normalize(a.entity) || key === normalize(a.label));
        const bIndex = wantedOrder.findIndex((key) => key === normalize(b.entity) || key === normalize(b.label));
        return aIndex - bIndex;
      });
  }, [mobileCardColumns, visibleColumns]);

  const tableData = useMemo(() => data || [], [data]);

  const totalPage = useMemo(() => {
    const count = pg?.count ?? 0;
    const per = pg?.itemPerPage ?? 1;
    return Math.max(1, Math.ceil(count / per));
  }, [pg?.count, pg?.itemPerPage]);

  const applyGridChange = (nextColumns: IGridItems[], persist = false) => {
    if (persist) {
      axiosInstance.post(ApiConstants.utility.uploadCT, { et: gridEntity, items: nextColumns.filter((c) => !isSyntheticColumn(c.entity)) }).catch(() => {});
    }
    onFilterClose?.(nextColumns);
  };

  const handlePageChange = (_: any, value: number) => {
    const clamped = Math.max(1, Math.min(value, totalPage));
    const next = { ...pg, page: clamped };
    setPg(next);
    selectedItemPagination(next);
  };

  const handleItemsPerPageChange = (event: any) => {
    const next = { ...pg, page: 1, itemPerPage: event.target.value ?? 50 };
    setPg(next);
    selectedItemPagination(next);
  };

  const getNextSort = (current?: string | null) => {
    const normalized = (current ?? '').toLowerCase();
    if (normalized === 'desc') return 'asc';
    return 'desc';
  };

  const handleSortClick = (columnEntity: string) => {
    if (isSyntheticColumn(columnEntity)) {
      return;
    }

    const targetColumn = columns.find((c) => c.entity === columnEntity);
    const nextSort = getNextSort(targetColumn?.sort);
    const nextColumns = columns.map((c) => (c.entity === columnEntity ? { ...c, sort: nextSort } : { ...c, sort: null }));
    applyGridChange(nextColumns, false);
  };

  const getSortIndicator = (sort?: string | null) => {
    const normalized = (sort ?? '').toLowerCase();
    if (normalized === 'asc') return <span className="gtSortArrow">↑</span>;
    if (normalized === 'desc') return <span className="gtSortArrow">↓</span>;
    return null;
  };

  const buildColumnsWithCurrentCustomization = () => {
    return columns.map((c) => {
      if (isSyntheticColumn(c.entity)) {
        return { ...c, filter: null };
      }

      const isFilterActive = activeFilterEntities.includes(c.entity);

      return {
        ...c,
        isShown: !hiddenColumns.includes(c.entity),
        filter: isFilterActive
          ? {
              value: '',
              operator: c.filter?.operator ?? 'contains',
            }
          : null,
      };
    });
  };

  const saveCustomization = () => {
    applyGridChange(buildColumnsWithCurrentCustomization(), true);
    setCustomizeOpen(false);
  };

  const handleQuickFilterInputChange = (entity: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [entity]: value }));

    if (quickFilterDebounceRef.current) {
      clearTimeout(quickFilterDebounceRef.current);
    }

    quickFilterDebounceRef.current = setTimeout(() => {
      const nextColumns = columns.map((c) => {
        if (isSyntheticColumn(c.entity)) {
          return { ...c, filter: null };
        }

        if (!activeFilterEntities.includes(c.entity)) {
          return { ...c, filter: null };
        }

        const currentValue = (c.entity === entity ? value : (columnFilters[c.entity] ?? '')).trim();
        return {
          ...c,
          filter: {
            value: currentValue,
            operator: c.filter?.operator ?? 'contains',
          },
        };
      });

      applyGridChange(nextColumns, false);
      axiosInstance
        .post(ApiConstants.utility.uploadCT, {
          et: gridEntity,
          items: buildColumnsWithCurrentCustomization().filter((c) => !isSyntheticColumn(c.entity)),
        })
        .catch(() => {});
    }, 1000);
  };

  const toggleActiveFilterEntity = (entity: string) => {
    setActiveFilterEntities((prev) => {
      if (prev.includes(entity)) return prev.filter((x) => x !== entity);
      if (prev.length >= MAX_ACTIVE_FILTERS) return prev;
      return [...prev, entity];
    });
  };

  const getValueByCaseInsensitiveKey = (obj: any, key: string) => {
    const keys = key.split('.');
    return keys.reduce((acc, curr) => {
      if (acc && typeof acc === 'object') {
        if (curr in acc) {
          return acc[curr];
        }

        const normalized = curr.toLowerCase();
        const matched = Object.keys(acc).find((k) => k.toLowerCase() === normalized);
        return matched ? acc[matched] : null;
      }
      return null;
    }, obj);
  };

  const hasActiveFilters = activeFilterEntities.length > 0;
  const shouldShowMobileFilterToggle = isMobileOrTablet && hasActiveFilters;
  const shouldShowDesktopQuickFilters = !isMobileOrTablet && hasActiveFilters;
  const shouldShowMobileQuickFilters = isMobileOrTablet && hasActiveFilters && showMobileQuickFilters;

  const renderQuickFilters = (containerClassName: string) => (
    <div className={containerClassName}>
      {activeFilterEntities.map((entity) => {
        const col = columns.find((c) => c.entity === entity);
        if (!col) return null;

        return (
          <div key={entity} className="gtQuickFilterItem">
            <input
              className="ui-control ui-input"
              placeholder={i18n.t(`entity.${col.label}`)}
              value={columnFilters[entity] ?? ''}
              onChange={(e) => handleQuickFilterInputChange(entity, e.target.value)}
            />
          </div>
        );
      })}
    </div>
  );

  const canShowCompleteAction = (row: any): boolean => {
    const numericStatus = Number(row?.orderStatus ?? row?.status);
    if (!Number.isNaN(numericStatus)) {
      return numericStatus === 1 || numericStatus === 2;
    }

    const normalize = (value: unknown) =>
      String(value ?? '')
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const statusText = normalize(row?.statusDesc || row?.orderStatusDesc || row?.status);
    return statusText === 'active' || statusText === 'pending' || statusText === 'ne pritje';
  };

  const hasRowActions = Boolean(onEdit || onDelete || (isCompleteShown && setComplete) || extraActions.length);
  const getItemClassName = (baseClassName: string, row: any) => `${baseClassName} ${getRowClassName?.(row) ?? ''}`.trim();

  const CardsView = () =>
    !tableData.length ? (
      <div className="gtEmpty">No data available</div>
    ) : (
      <div className="gtCards">
        {tableData.map((row, rowIndex) => (
          <div key={rowIndex} className={getItemClassName('gtCard', row)} onClick={() => selectedItem(row)} role="button" tabIndex={0}>
            <div className="gtCardMain">
              <div className="gtCardGrid">
              {mobileCardVisibleColumns.map((col) => (
                  <div key={col.entity} className={getMobileCardFieldClassName(row, col)} data-entity={col.entity.toLowerCase()}>
                    <div className="gtCardLabel">{i18n.t(`entity.${col.label}`)}</div>
                    <div className="gtCardValue">{renderCellValue(row, col, false)}</div>
                  </div>
                ))}
              </div>
            </div>

            {hasRp && hasRowActions && (
              <div className="gtCardActionsInline" onClick={(e) => e.stopPropagation()}>
                {onEdit && (canEdit?.(row) ?? true) && (
                  <Tooltip title="Edit">
                    <IconButton onClick={() => onEdit?.(row)} className="gtIconBtn" size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {onDelete && (canDelete?.(row) ?? true) && (
                  <Tooltip title="Delete">
                    <IconButton onClick={() => onDelete?.(row)} className="gtIconBtn isDanger" size="small">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                {isCompleteShown && canShowCompleteAction(row) && (
                  <Tooltip title="Complete">
                    <IconButton onClick={() => setComplete?.(row.id)} className="gtIconBtn" size="small">
                      <DoneAllIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {extraActions
                  .filter((action) => !action.show || action.show(row))
                  .map((action) => (
                    <Tooltip title={action.title} key={action.key}>
                      <IconButton onClick={() => action.onClick(row)} className={`gtIconBtn ${action.className ?? ''}`.trim()} size="small">
                        {action.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );

  const TableView = () =>
    !tableData.length ? (
      <div className="gtEmpty">No data available</div>
    ) : (
      <TableContainer component={Paper} className="gtTableContainer">
        <Table stickyHeader className="gtTable">
          <TableHead>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableCell
                  key={col.entity}
                  className="gtTh"
                  onClick={() => handleSortClick(col.entity)}
                  style={{ cursor: isSyntheticColumn(col.entity) ? 'default' : 'pointer' }}
                >
                  {i18n.t(`entity.${col.label}`)}
                  {getSortIndicator(col.sort)}
                </TableCell>
              ))}
              {hasRp && hasRowActions && <TableCell className="gtTh">{i18n.t('actions.actions')}</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.map((row, rowIndex) => (
              <TableRow hover key={rowIndex} className={getItemClassName('gtTr', row)}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.entity} onClick={() => selectedItem(row)} className="gtTd gtClickable">
                  {renderCellValue(row, col)}
                </TableCell>
              ))}

                {hasRp && hasRowActions && (
                  <TableCell className="gtTd">
                    <div className="gtActions">
                      {onEdit && (canEdit?.(row) ?? true) && (
                        <Tooltip title={i18n.t('actions.edit')}>
                          <IconButton onClick={() => onEdit?.(row)} className="gtIconBtn">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDelete && (canDelete?.(row) ?? true) && (
                        <Tooltip title={i18n.t('actions.delete')}>
                          <IconButton onClick={() => onDelete?.(row)} className="gtIconBtn isDanger">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isCompleteShown && canShowCompleteAction(row) && (
                        <Tooltip title={i18n.t('generic.complete')}>
                          <IconButton onClick={() => setComplete?.(row.id)} className="gtIconBtn">
                            <DoneAllIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {extraActions
                        .filter((action) => !action.show || action.show(row))
                        .map((action) => (
                          <Tooltip title={action.title} key={action.key}>
                            <IconButton onClick={() => action.onClick(row)} className={`gtIconBtn ${action.className ?? ''}`.trim()}>
                              {action.icon}
                            </IconButton>
                          </Tooltip>
                        ))}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );

  return (
    <div className="general-table">
      <div className="gtTop">
        <div className="gtTopLeft">
          {title && <h4 className="gtTitle">{title}</h4>}

          <button className="ui-control ui-btn ui-btn--secondary" onClick={() => setCustomizeOpen(true)} type="button">
            {i18n.t('generic.filter')} / {i18n.t('generic.columns')}
          </button>

          {children}

          {shouldShowMobileFilterToggle && (
            <button className="ui-control ui-btn ui-btn--secondary" onClick={() => setShowMobileQuickFilters((prev) => !prev)} type="button">
              {showMobileQuickFilters ? `${i18n.t('generic.filter')} - Hide` : `${i18n.t('generic.filter')} - Show`}
            </button>
          )}

          {shouldShowDesktopQuickFilters && renderQuickFilters('gtQuickFiltersInline')}
        </div>
      </div>

      {shouldShowMobileQuickFilters && renderQuickFilters('gtQuickFiltersMobile')}

      <div className="gtBody">
        {isMobileOrTablet ? <CardsView /> : <TableView />}
      </div>

      <div className="gtBottomBar">
        <div className="gtBottomControls">
          <div className="gtPageStepper">
            <Pagination count={totalPage} page={pg.page} onChange={handlePageChange} className="gtPagination" />
          </div>
          <FormControl className="gtPerPageWrap">
            <InputLabel id="per-page-label" className="gtLabel">
              {i18n.t('generic.rows')}
            </InputLabel>
            <Select
              labelId="per-page-label"
              value={pg.itemPerPage}
              label={i18n.t('generic.rows')}
              onChange={handleItemsPerPageChange}
              className="gtPerPage gtSelect"
              input={<OutlinedInput className="gtOutlinedInput" />}
              MenuProps={{
                disablePortal: true,
                PaperProps: { className: 'gtMenuPaper' },
                MenuListProps: { className: 'gtMenuList' },
              }}
            >
              {pg.itemsPerPage.map((option) => (
                <MenuItem key={option} value={option} className="gtMenuItem">
                  {option} / {i18n.t('generic.page')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: `gtCustomizeDialogPaper bcTheme-${theme}` }}
      >
        <DialogTitle className="gtCustomizeDialogTitle">
          {i18n.t('generic.filter')} / {i18n.t('generic.columns')}
        </DialogTitle>
        <DialogContent className="gtCustomizeDialogBody">
          <div className="gtCustomizeTabs">
            <button
              type="button"
              className={`ui-control ui-btn ${customizeTab === 'columns' ? 'ui-btn--primary' : 'ui-btn--secondary'}`}
              onClick={() => setCustomizeTab('columns')}
            >
              {i18n.t('generic.columns')}
            </button>
            <button
              type="button"
              className={`ui-control ui-btn ${customizeTab === 'filters' ? 'ui-btn--primary' : 'ui-btn--secondary'}`}
              onClick={() => setCustomizeTab('filters')}
            >
              {i18n.t('generic.filter')}
            </button>
          </div>

          {customizeTab === 'columns' ? (
            <div className="gtCustomizeList">
              {columns.map((col) => (
                <label key={col.entity} className="gtCustomizeRow">
                  <Checkbox
                    className="gtChk"
                    checked={!hiddenColumns.includes(col.entity)}
                    onChange={() => {
                      setHiddenColumns((prev) => (prev.includes(col.entity) ? prev.filter((x) => x !== col.entity) : [...prev, col.entity]));
                    }}
                  />
                  <span>{i18n.t(`entity.${col.label}`)}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="gtCustomizeList">
              <div className="gtFilterHint">Max {MAX_ACTIVE_FILTERS} filters per grid.</div>
              {columns.filter((col) => !isSyntheticColumn(col.entity)).map((col) => {
                const isChecked = activeFilterEntities.includes(col.entity);
                const canEnable = isChecked || activeFilterEntities.length < MAX_ACTIVE_FILTERS;

                return (
                  <label key={col.entity} className="gtCustomizeRow">
                    <Checkbox className="gtChk" checked={isChecked} disabled={!canEnable} onChange={() => toggleActiveFilterEntity(col.entity)} />
                    <span>{i18n.t(`entity.${col.label}`)}</span>
                  </label>
                );
              })}
            </div>
          )}
        </DialogContent>
        <DialogActions className="gtCustomizeDialogActions">
          <Button onClick={() => setCustomizeOpen(false)}>{i18n.t('actions.cancel')}</Button>
          <Button onClick={saveCustomization} variant="contained">
            {i18n.t('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GeneralTable;
